import { t } from '../../i18n';
import type { CompanyRole, StaffRoleRef, SystemRoleKey } from '../../services/roleService';
import type { ContractHoursBand, SiteBinding, StaffRole } from '../../services/staffService';

/** Shared wording for the staff directory, the add-user dialog, the profile and the roles page. */

/** The tier somebody's roles add up to. Used where the roles themselves are not to hand. */
export const staffRoleLabel = (role: StaffRole): string => {
  switch (role) {
    case 'admin': return t('Admin||role');
    case 'manager': return t('Manager');
    default: return t('Guard');
  }
};

/** What each seeded role is called until a company renames it. */
const SEEDED_ROLE_NAMES: Record<SystemRoleKey, string> = {
  guard_rank_1: 'Guard Rank 1',
  guard_rank_2: 'Guard Rank 2',
  ops_manager: 'Ops Manager',
  system_admin: 'System Administrator',
};

const SEEDED_ROLE_DESCRIPTIONS: Record<SystemRoleKey, string> = {
  guard_rank_1: 'Entry-level guard.',
  guard_rank_2: 'Senior guard.',
  ops_manager: 'Runs day-to-day operations from the admin dashboard.',
  system_admin: 'Full access to every service and permission.',
};

/**
 * A role's name is the company's own data and is shown as written. The
 * seeded ones are translated for as long as they keep the name they came
 * with; once renamed, the company's wording wins.
 */
export const roleDisplayName = (role: Pick<StaffRoleRef, 'name' | 'systemKey'>): string => (
  role.systemKey && role.name === SEEDED_ROLE_NAMES[role.systemKey] ? t(role.name) : role.name
);

export const roleDescription = (role: Pick<CompanyRole, 'description' | 'systemKey'>): string => {
  if (!role.description) return '';
  return role.systemKey && role.description === SEEDED_ROLE_DESCRIPTIONS[role.systemKey]
    ? t(role.description)
    : role.description;
};

/** One line on what holding a kind of role means, shown where roles are being chosen. */
export const roleKindSummary = (kind: StaffRoleRef['kind']): string => (
  kind === 'guard'
    ? t('Works shifts and uses the mobile app.')
    : t('Signs in to this workspace and sees the areas the role opens.')
);

export const formatHourlyRate = (rate: number): string => rate.toFixed(2);

/** "20-25 hours a week", or the casual contract that guarantees nothing. */
export const contractBandLabel = (band: ContractHoursBand): string => (
  band.maxHours === null
    ? t('No guaranteed hours')
    : t('{min}-{max} hours a week', { min: band.minHours, max: band.maxHours })
);

/** A bound client reads as all of its sites; a bound site by its own name. */
export const siteBindingLabel = (binding: Pick<SiteBinding, 'kind' | 'name' | 'archived'>): string => {
  const label = binding.kind === 'client'
    ? t('{client}: all sites', { client: binding.name })
    : binding.name;
  return binding.archived ? t('{name} (archived)', { name: label }) : label;
};

export const getStaffInitials = (
  name: string | null | undefined,
  email?: string | null,
): string => {
  const source = name?.trim() || email?.trim();
  if (!source) return '?';
  return source
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0] ?? '')
    .join('')
    .toUpperCase();
};
